'use client'

import type { MouseEvent, ReactNode } from 'react'

/** How long the app gets to take focus before the web fallback opens. */
const APP_OPEN_WAIT_MS = 1500

/**
 * A link to a desktop app's URL scheme (`claude://…`) with a web fallback.
 * The anchor itself points at `fallbackHref`, so a modified or middle
 * click (new tab, new window) and a visitor without JavaScript land on the
 * web app. A plain click hands the scheme to the browser from this tab (an
 * app scheme never opens a blank tab); if the app is installed the page
 * loses focus and nothing else happens. If the page still has focus after
 * `APP_OPEN_WAIT_MS` (no app took the link), `fallbackHref` opens in a new
 * tab instead, so a visitor without the app still lands somewhere useful.
 */
export function AppSchemeLink({
  href,
  fallbackHref,
  className,
  'aria-label': ariaLabel,
  children,
}: {
  href: string
  fallbackHref: string
  className?: string
  'aria-label'?: string
  children: ReactNode
}) {
  const open = (event: MouseEvent<HTMLAnchorElement>) => {
    // A modified or non-primary click keeps the browser's own behavior on
    // the web fallback; only a plain click runs the app handoff.
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return
    }
    event.preventDefault()
    let isAppOpen = false
    const markOpen = () => {
      isAppOpen = true
    }
    window.addEventListener('blur', markOpen, { once: true })
    document.addEventListener('visibilitychange', markOpen, { once: true })
    window.location.href = href
    window.setTimeout(() => {
      window.removeEventListener('blur', markOpen)
      document.removeEventListener('visibilitychange', markOpen)
      if (!isAppOpen && document.hasFocus()) {
        window.open(fallbackHref, '_blank', 'noopener,noreferrer')
      }
    }, APP_OPEN_WAIT_MS)
  }
  return (
    <a
      href={fallbackHref}
      onClick={open}
      aria-label={ariaLabel}
      className={className}
    >
      {children}
    </a>
  )
}
